import { renovar } from '@/features/autenticacao/api/autenticacaoApi';
import {
  definirSessao,
  deRespostaDeLogin,
  lerAccessToken,
  lerSessao,
  type SessaoAtiva,
} from '@/features/autenticacao/sessaoEmMemoria';
import { definirProvedorDeToken, definirTratadorDeNaoAutorizado } from '@/lib/http';
import { podeExistirSessao } from '@/lib/preferencias';

/**
 * Renovacao da sessao.
 *
 * O SERVIDOR ROTACIONA: cada chamada a /renovar revoga o token apresentado, emite
 * outro e substitui o cookie. Duas chamadas com o MESMO token fazem o servidor
 * concluir que existem duas copias em circulacao e revogar a familia inteira — ou
 * seja, derrubam a sessao do usuario legitimo.
 *
 * Isso torna a serializacao obrigatoria, e nao uma otimizacao. Uma tela que dispara
 * tres consultas ao montar recebe tres 401 quase simultaneos; sem a promessa
 * compartilhada abaixo, as tres chegariam ao servidor com o cookie antigo, porque o
 * cookie novo so passa a valer depois da primeira resposta. A segunda seria lida
 * como reuso.
 *
 * O sintoma seria brutal de diagnosticar: logout aparentemente aleatorio, so em
 * telas com varias consultas, e com o log do servidor acusando ataque.
 */

let renovacaoEmAndamento: Promise<SessaoAtiva | null> | null = null;

/**
 * Desliga as tentativas depois de uma recusa.
 *
 * Sem isto, cada 401 subsequente tentaria renovar de novo. Como o limitador de taxa
 * do servidor conta 30 tentativas por 5 minutos POR ORIGEM, e os usuarios estao
 * atras do mesmo NAT corporativo, uma aplicacao aberta em varias abas gastaria o
 * balde compartilhado e devolveria 429 para quem esta tentando entrar.
 *
 * Volta a valer no proximo login.
 */
let renovacaoRecusada = false;

/**
 * Renova a sessao no maximo uma vez por vez.
 *
 * Chamadas concorrentes recebem a mesma promessa. Devolve a sessao nova, ou nulo
 * quando nao foi possivel — e nesse caso a sessao local ja foi limpa.
 */
export function renovarSessao(): Promise<SessaoAtiva | null> {
  if (renovacaoEmAndamento === null) {
    renovacaoEmAndamento = executarRenovacao().finally(() => {
      // Libera a referencia em qualquer desfecho. Sem isto, uma falha transitoria
      // de rede deixaria a aplicacao presa numa promessa rejeitada para sempre.
      renovacaoEmAndamento = null;
    });
  }

  return renovacaoEmAndamento;
}

async function executarRenovacao(): Promise<SessaoAtiva | null> {
  // Nao ha como olhar o cookie: ele e HttpOnly. A marca de sessao responde se vale
  // gastar a requisicao. Ver lib/preferencias.
  if (renovacaoRecusada || !podeExistirSessao()) {
    definirSessao(null);

    return null;
  }

  try {
    const sessao = deRespostaDeLogin(await renovar());

    definirSessao(sessao);
    renovacaoRecusada = false;

    return sessao;
  } catch {
    // Token desconhecido, expirado, familia vencida e reuso detectado respondem
    // igual, de proposito. Do lado do cliente o tratamento e o mesmo: a sessao
    // acabou.
    renovacaoRecusada = true;
    definirSessao(null);

    return null;
  }
}

/**
 * Restaura a sessao no carregamento da pagina.
 *
 * O access token morreu com o recarregamento; o cookie sobreviveu. Esta e a
 * chamada que troca um pelo outro.
 *
 * Roda no maximo uma vez por carregamento. O StrictMode do React monta os efeitos
 * duas vezes em desenvolvimento, e duas renovacoes com o mesmo cookie seriam lidas
 * como reuso — que revoga a familia. A promessa compartilhada de `renovarSessao` ja
 * cobriria o caso concorrente, mas a segunda montagem pode acontecer depois de a
 * primeira terminar, e ai a protecao seria a marca de sessao, nao a promessa. Este
 * indicador fecha os dois caminhos.
 */
let restauracaoIniciada = false;

export function restaurarSessao(): Promise<SessaoAtiva | null> {
  if (restauracaoIniciada) {
    return Promise.resolve(lerSessao());
  }

  restauracaoIniciada = true;

  return renovarSessao();
}

/** Permite ao login voltar a tentar depois de uma recusa. */
export function liberarRenovacao(): void {
  renovacaoRecusada = false;
}

/**
 * Instala o provedor de token e o interceptor de 401 no cliente HTTP.
 *
 * `aoPerderSessao` conduz o usuario de volta ao login. A navegacao nao acontece
 * aqui porque este modulo nao conhece o roteador.
 */
export function instalarInterceptorDeSessao(aoPerderSessao: () => void): () => void {
  definirProvedorDeToken(lerAccessToken);

  definirTratadorDeNaoAutorizado(async (repetir) => {
    const sessao = await renovarSessao();

    if (sessao === null) {
      aoPerderSessao();

      return null;
    }

    // `repetir` reexecuta a requisicao original, e o cabecalho Authorization e
    // remontado a partir do provedor — portanto ja com o token novo.
    //
    // Uma unica tentativa. Se este 401 voltar, ele sobe como ErroDeApi: repetir de
    // novo so produziria laco, porque a causa deixou de ser token vencido.
    return repetir();
  });

  return () => {
    definirTratadorDeNaoAutorizado(null);
  };
}

/** Reinicia o estado do modulo. Somente para teste. */
export function reiniciarRenovacaoParaTeste(): void {
  renovacaoEmAndamento = null;
  renovacaoRecusada = false;
  restauracaoIniciada = false;
}
